import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DepreciationMethodSettings } from '@assetaccountant/assets/models/depreciation-method-settings';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import {
  AzureSearchResult,
  HighlightedResult,
  InfiniteResult,
} from '../models/azure-search.models';
import { RegimeDepreciationMethodsDtoWithKey } from '../models/depreciation-method';
import { GetRateApiModelRequest } from '../models/get-rate-api-model-request';
import { GetRateApiModelResponse } from '../models/get-rate-api-model-response';
import { RegimeSettingApiModel } from '../models/regime-setting-api-model';
import { CompareFn, SORT_COMPARE } from './compare.factory';

export interface RateLookupBase {
  life: number | null;
  rates?: Array<{
    code: string;
    name: string;
    methodCodeRegex: string;
    rate?: number;
    life?: number;
  }>;
  href: string;
  ruling: string;
}

export interface RateLookupResult extends RateLookupBase {
  asset: string;
  industry: string;
  categories: Array<{ name: string; footnote?: string }>;
  effectiveDate: string;
  footnote?: string;
  alternates?: Array<
    RateLookupBase & {
      label: string;
    }
  >;
}

@Injectable({
  providedIn: 'root',
})
export class DepreciationService {
  constructor(
    @Inject(SORT_COMPARE) private sortCompare: CompareFn,
    private http: HttpClient,
  ) {}

  getDepreciationMethods(): Observable<RegimeDepreciationMethodsDtoWithKey[]> {
    return this.http.get<RegimeDepreciationMethodsDtoWithKey[]>(
      `${environment.servicesApiUrl}/api/depreciation/method-list`,
    );
  }

  getRegimeSettings(regimeId?: number): Observable<RegimeSettingApiModel[]> {
    return this.http.get<RegimeSettingApiModel[]>(
      `${environment.servicesApiUrl}/api/depreciation/regime-settings`,
      {
        headers: regimeId
          ? {
              RegimeId: regimeId.toString(),
            }
          : undefined,
      },
    );
  }

  getRate<TRequest extends GetRateApiModelRequest>(request: TRequest) {
    const params = Object.fromEntries(
      Object.entries(request)
        .map(([key, value]) => [key, value?.toString() as string | undefined] as const)
        .filter((kv): kv is [string, string] => !!kv[1]),
    );

    return this.http.get<GetRateApiModelResponse<TRequest>>(
      `${environment.servicesApiUrl}/api/depreciation/rate`,
      {
        params,
      },
    );
  }

  lookupIndustries(regimeId: number) {
    const filter = `regimeId eq ${regimeId}`;

    return this.lookupRateInternal({
      filter,
      facets: ['industryPath,count:1000'],
      top: 0,
    }).pipe(map(({ industries }) => industries));
  }

  lookupRate(search: string, regimeId: number, industry: string | null = null) {
    let filter = `regimeId eq ${regimeId}`;
    let orderby: string | undefined;
    if (industry != null) {
      filter += ` and (industryPath/any(i: i eq '${industry}') or industry eq '')`;
      orderby = 'industry desc';
    }

    return this.lookupRateInternal({
      search,
      searchMode: 'all',
      filter,
      orderby,
      highlight: 'asset, categories/name, industry',
      highlightPreTag: '<mark>',
      highlightPostTag: '</mark>',
      facets: ['industryPath,count:1000', 'rates/name'],
    });
  }

  private lookupRateInternal(body: Record<string, any>): Observable<
    InfiniteResult<{
      industries: string[];
      rates: string[];
      results: Array<HighlightedResult<RateLookupResult>>;
    }>
  > {
    // Via the API (RatesController), which authenticates the user and forwards the body to the
    // internal fq-fixedassets-rates service. Being under servicesApiUrl, the request picks up the
    // same auth as every other API call (HttpAuthInterceptor).
    return this.http
      .post<
        AzureSearchResult<
          RateLookupResult,
          'asset' | 'categories/name' | 'industry',
          { industryPath: true; 'rates/name': true }
        >
      >(`${environment.servicesApiUrl}/api/rates/search`, body)
      .pipe(
        map(x => {
          const industries = x['@search.facets'].industryPath
            .map(f => f.value)
            .sort(this.sortCompare)
            // put empty last
            .sort((a, b) => (!!a === !!b ? 0 : !a ? 1 : -1));
          const rates = x['@search.facets']['rates/name']?.map(f => f.value).sort(this.sortCompare);

          const results = x.value.map(
            (azureResult): HighlightedResult<RateLookupResult> => ({
              ...azureResult,
              highlights: {
                asset: applyHighlights(azureResult.asset, azureResult['@search.highlights']?.asset),
                industry: applyHighlights(
                  azureResult.industry,
                  azureResult['@search.highlights']?.industry,
                ),
                categories: azureResult.categories.map(c => ({
                  name: applyHighlights(
                    c.name,
                    azureResult['@search.highlights']?.['categories/name'],
                  ),
                  footnote: c.footnote,
                })),
              },
            }),
          );

          function applyHighlights(value: string, highlights?: string[]) {
            let result = value;
            highlights?.forEach(marked => {
              const unmarked = marked.replace(/<\/?mark>/g, '');
              result = result.replace(unmarked, marked);
            });
            return result;
          }

          return {
            industries,
            rates,
            results,
            next$: x['@search.nextPageParameters']
              ? this.lookupRateInternal(x['@search.nextPageParameters'])
              : undefined,
          };
        }),
      );
  }

  getDepreciationSettings(
    regimes: RegimeDepreciationMethodsDtoWithKey[],
    regimeId: number,
    methodId?: number | string | null,
  ): DepreciationMethodSettings {
    const defaultSettings = DepreciationMethodSettings.default();

    const regimeDepreciationMethod = regimes.find(r => r.regimeId === regimeId);
    const method = regimeDepreciationMethod?.methods.find(m => m.id === methodId);
    if (!method) {
      return defaultSettings;
    }

    return {
      ...method,
      isUnitsOfUse: method.code === 'UU',
      isLifeFreeInput: method.lifeValues == null || method.lifeValues.length === 0,
    };
  }
}
